/**
 * EnhancedResultsScreen - Detailed score breakdown shown after game completion
 * Replaces the simple mission summary with personal score analysis
 */

import { GameState } from '@/game/models/GameState';
import { PlayerState } from '@/game/models/Player';
import { scoringSystem } from '@/game/engine/ScoringSystem';
import { Button } from '@/components/ui/button';
import { IoHappy, IoHeartDislike } from 'react-icons/io5';

interface EnhancedResultsScreenProps {
  gameState: GameState;
  playerState: PlayerState;
  sessionCode?: string;
  onJoinAnother: () => void;
}

const DIMENSION_CONFIG = {
  brain: { label: 'Brain', description: 'Problem Solving', color: 'bg-[#12356B]', bgColor: 'bg-[#12356B]/10', textColor: 'text-[#12356B]' },
  heart: { label: 'Heart', description: 'Relationships', color: 'bg-[#DD6832]', bgColor: 'bg-[#DD6832]/10', textColor: 'text-[#DD6832]' },
  task: { label: 'Task', description: 'Task Completion', color: 'bg-[#367A25]', bgColor: 'bg-[#367A25]/10', textColor: 'text-[#367A25]' },
  communication: { label: 'Communication', description: 'Info Sharing', color: 'bg-[#12356B]', bgColor: 'bg-[#E2E5EC]', textColor: 'text-[#12356B]' },
} as const;

export function EnhancedResultsScreen({
  gameState,
  playerState,
  onJoinAnother,
}: EnhancedResultsScreenProps) {
  const missionSuccess = gameState.gameStatus === 'completed';
  const summary = scoringSystem.generateScoreSummary(playerState);

  return (
    <div className="flex flex-col items-center justify-center py-8 text-center max-w-2xl mx-auto">
      {/* Mission outcome header */}
      <div className={`size-24 rounded-full bg-gradient-to-br ${
        missionSuccess
          ? 'from-[#367A25] to-[#2d6820]'
          : 'from-[#DD6832] to-[#c4562a]'
      } flex items-center justify-center mb-6 shadow-lg`}>
        <span className="text-5xl text-white">
          {missionSuccess ? <IoHappy /> : <IoHeartDislike />}
        </span>
      </div>
      <h2 className="text-3xl font-bold mb-2 text-[#12356B]">
        {missionSuccess ? 'Mission Complete!' : 'Mission Failed'}
      </h2>
      <p className="text-lg text-slate-600 mb-8">
        {missionSuccess
          ? 'Congratulations! Your team has successfully completed the mission.'
          : 'Unfortunately, your mission was not successful.'}
      </p>

      {/* Personal Score Breakdown */}
      <div className="bg-[#E2E5EC]/70 border border-[#CED4DE] rounded-xl p-6 w-full mb-6">
        <h3 className="font-semibold text-lg mb-1 text-[#12356B]">Your Leadership Score</h3>
        <p className="text-4xl font-bold mb-1 text-[#12356B]">{summary.total}</p>
        <p className="text-sm text-slate-600 mb-4">{summary.category}</p>

        {/* Dimension cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {(Object.keys(DIMENSION_CONFIG) as Array<keyof typeof DIMENSION_CONFIG>).map((dim) => {
            const config = DIMENSION_CONFIG[dim];
            const score = summary.scores[dim];
            const pct = summary.breakdown[dim];
            return (
              <div key={dim} className={`${config.bgColor} rounded-lg p-3 text-left`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-semibold ${config.textColor}`}>{config.label}</span>
                  <span className="text-lg font-bold">{score}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{config.description}</p>
                <div className="w-full bg-white/80 rounded-full h-2">
                  <div
                    className={`${config.color} rounded-full h-2 transition-all`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Leadership style */}
        <div className="bg-white rounded-lg p-3 text-left border border-[#CED4DE]">
          <p className="text-sm text-slate-600">Leadership Style</p>
          <p className="font-medium text-[#12356B]">{summary.leadershipStyle}</p>
        </div>
      </div>

      {/* Mission Summary */}
      <div className="bg-[#E2E5EC]/70 border border-[#CED4DE] rounded-xl p-6 w-full mb-6">
        <h3 className="font-semibold mb-4 text-[#12356B]">Mission Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-left">
          <div className="bg-white border border-[#CED4DE] rounded-lg p-3">
            <p className="text-sm text-slate-600">Final Stage</p>
            <p className="font-medium text-[#12356B]">Stage {gameState.currentStage}</p>
          </div>
          <div className="bg-white border border-[#CED4DE] rounded-lg p-3">
            <p className="text-sm text-slate-600">Permit Day</p>
            <p className="font-medium text-[#12356B]">Day {gameState.huntingPermitDay} of 6</p>
          </div>
          <div className="bg-white border border-[#CED4DE] rounded-lg p-3">
            <p className="text-sm text-slate-600">Team Energy</p>
            <p className="font-medium text-[#12356B]">{gameState.teamEnergyPacks} packs remaining</p>
          </div>
          <div className="bg-white border border-[#CED4DE] rounded-lg p-3">
            <p className="text-sm text-slate-600">Crew Status</p>
            <p className="font-medium text-[#12356B]">
              {gameState.players.filter(p => p.isAlive).length}/{gameState.players.length} survived
            </p>
          </div>
        </div>
      </div>

      <Button onClick={onJoinAnother} size="lg" className="bg-[#367A25] hover:bg-[#2d6820] text-white">
        Join Another Session
      </Button>
    </div>
  );
}
